import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useFetchDataImmutable } from "../../apiHelpers";
import { API_ROUTES } from "../../constants";
import { appendParamsToUrl, toDollar } from "../../helpers";
import useOutputReportStore from "../../store/OutputReport.store";
import { getValidCustomizations } from "../../utils/helpers/intakeForm";
import {
  AVAILABLE_SECTIONS_PER_REPORT_METADATA,
  ENABLE_OUTPUT_REPORT_CUSTOMIZER,
} from "../../utils/reports";
import Button from "../Button";
import Dropdown from "../Dropdown";
import Logo from "../Logo";
import OutputReportCustomizer from "../OutputReportCustomizer";
import Skeleton from "../Skeleton";

import "./reportHeader.scss";

const ReportHeader = () => {
  //#region //! Page path, general states and utilities
  const navigate = useNavigate();
  const [isCustomizerOpen, setIsCustomizerOpen] = useState(false);
  //#endregion

  //#region //! Get store data
  const { metadata } = useOutputReportStore();
  const coverData = metadata?.Cover || {};
  const reportType = metadata?.Cover["Report Type"].toLowerCase();
  const assetType = metadata?.Cover["Asset Type"].toLowerCase();
  const { TransitionId: proposalId } = coverData || {};
  //#endregion

  //#region //! Fetching data
  const {
    data: fetchedProposalDetails,
    isLoading: fetchedProposalDetailsIsLoading,
  } = useFetchDataImmutable(
    {
      url: appendParamsToUrl(API_ROUTES.PROPOSAL_DETAIL, {
        transitionId: proposalId,
      }),
    },
    !!proposalId
  );
  //#endregion

  if (
    !metadata ||
    !coverData ||
    fetchedProposalDetailsIsLoading ||
    !fetchedProposalDetails
  ) {
    return null;
  }

  //#region //! Initialize data
  const proposalCustomizations = getValidCustomizations(fetchedProposalDetails);

  const headerData = {
    client: coverData?.Client,
    strategy: coverData?.Strategy,
    amount: toDollar(coverData?.["Initial Funding Amount"]),
  };

  const items = AVAILABLE_SECTIONS_PER_REPORT_METADATA[reportType]?.[assetType]
    .map(({ label, sectionMetadataKey, sectionKeyKebab }) => ({
      id: sectionKeyKebab,
      href: `#${sectionMetadataKey}`,
      text: label,
    }))
    .filter((item) => {
      return item.id !== "firm-overview" || item.id !== "definitions";
    });

  const showReportCustomizerModal = ENABLE_OUTPUT_REPORT_CUSTOMIZER && metadata;
  //#endregion

  return (
    <>
      <header className="report-header">
        <div className="report-header__container">
          <Logo />
          <div className="report-header__dropdown">
            <Skeleton
              size="small"
              settings={{ width: 168, height: 32 }}
              loaded={items.length > 0}
            >
              <Dropdown items={items} placeholder="One Sheeter" />
            </Skeleton>
          </div>
          <ul className="report-header__info">
            <li>
              <Skeleton
                size="small"
                settings={{ width: 132, height: 32 }}
                loaded={!!headerData.client}
              >
                <span className="report-header__heading">Prepared for</span>
                <span className="report-header__name">{headerData.client}</span>
              </Skeleton>
            </li>
            <li>
              <Skeleton
                size="small"
                settings={{ width: 132, height: 32 }}
                loaded={!!headerData.client}
              >
                <span className="report-header__heading">Strategy</span>
                <span className="report-header__name">
                  {headerData.strategy}
                </span>
              </Skeleton>
            </li>
            <li>
              <Skeleton
                size="small"
                settings={{ width: 132, height: 32 }}
                loaded={!!headerData.client}
              >
                <span className="report-header__heading">
                  Initial Funding Amount
                </span>
                <span className="report-header__name">{headerData.amount}</span>
              </Skeleton>
            </li>
          </ul>
          {ENABLE_OUTPUT_REPORT_CUSTOMIZER && (
            <Button
              variant="outline"
              className="button--customize"
              onClick={() => {
                setIsCustomizerOpen(true);
              }}
            >
              Customize & Print
            </Button>
          )}
        </div>
      </header>
      {showReportCustomizerModal && (
        <OutputReportCustomizer
          active={isCustomizerOpen}
          setIsCustomizerOpen={setIsCustomizerOpen}
          reportType={reportType}
          assetType={assetType}
          clientName={coverData.Client}
          advisorName={coverData.Advisor}
          customizations={proposalCustomizations}
        />
      )}
    </>
  );
};

export default ReportHeader;
